import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, CheckCircle, Target, Eye, Globe,
  Lightbulb, Award, Heart, Shield, Code, Cpu,
  Server, Layers, FastForward, Users, Briefcase,
  Zap, Star,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PageHero from "./PageHero";
import PortfolioFooter from "./PortfolioFooter";

const stats = [
  { value: "5+",   label: "Years of Excellence",    icon: <Award className="w-6 h-6 text-orange-400 mx-auto mb-2" /> },
  { value: "100+", label: "Projects Delivered",      icon: <Briefcase className="w-6 h-6 text-orange-400 mx-auto mb-2" /> },
  { value: "50+",  label: "Engineers & Designers",   icon: <Users className="w-6 h-6 text-orange-400 mx-auto mb-2" /> },
  { value: "4",    label: "Industries Served",       icon: <Globe className="w-6 h-6 text-orange-400 mx-auto mb-2" /> },
];

const values = [
  { icon: <Lightbulb className="w-7 h-7 text-orange-500" />, title: "Innovation First",    desc: "Constantly exploring emerging technologies to give clients a measurable competitive edge." },
  { icon: <Award className="w-7 h-7 text-orange-500" />,     title: "Engineering Quality", desc: "Unwavering commitment to clean code, rigorous testing, and superior system performance." },
  { icon: <Heart className="w-7 h-7 text-orange-500" />,     title: "Client Success",      desc: "Deeply invested in the technical success and long-term scalability of every product we touch." },
  { icon: <Shield className="w-7 h-7 text-orange-500" />,    title: "Security & Trust",    desc: "Upholding absolute confidentiality, robust security protocols, and industry compliance." },
];

const teamPillars = [
  { icon: <Cpu className="w-6 h-6 text-orange-500" />,         title: "Engineering Culture",  desc: "Passionate developers focused on clean code, modularity, and technical innovation." },
  { icon: <Layers className="w-6 h-6 text-orange-500" />,      title: "Full-Stack Expertise", desc: "Proficient across frontend, backend, mobile, and cloud for unified delivery." },
  { icon: <FastForward className="w-6 h-6 text-orange-500" />, title: "Agile Mindset",        desc: "Scrum and Kanban methodologies for rapid deployment and continuous improvement." },
  { icon: <Code className="w-6 h-6 text-orange-500" />,        title: "Modern Stack",         desc: "React, Node.js, Python, Go, Kubernetes, AWS — always current, always deliberate." },
  { icon: <Server className="w-6 h-6 text-orange-500" />,      title: "Cloud-Native Focus",   desc: "Containerisation and serverless architectures for maximum efficiency and scale." },
  { icon: <Zap className="w-6 h-6 text-orange-500" />,         title: "Observability",        desc: "Real-time system health via Grafana, Prometheus, and Datadog." },
];

const whyPoints = [
  "Senior engineers with 5+ years of SaaS and enterprise experience",
  "End-to-end ownership from architecture to post-launch support",
  "Transparent SDLC with weekly demos and milestone reports",
  "Security-first development with regular audits",
  "Dedicated account manager for every engagement",
  "Proven across Fintech, Healthcare, E-commerce, and EdTech",
];

const CompanyOverview = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans bg-white">
      <Helmet>
        <title>About Us | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Learn about Winsil Evolution — our mission, team culture, engineering values, and the story behind 5+ years of building scalable software and brand experiences." />
      </Helmet>

      <PortfolioNavbar />

      <PageHero
        label="Who We Are"
        labelClass="text-orange-400 font-black"
        title="About Winsil Evolution"
        subtitle="A premier software engineering and digital branding firm built on technical excellence, agile execution, and a relentless focus on client success."
        image="/images/photo-1522202176988-66273c2fd55f.jpg"
        imageAlt="Team collaborating on software project"
        bgClass="bg-gray-950"
        glowColor="#f97316"
        titleClass="text-white"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center text-orange-500 mb-2">Our World</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">People, Code & Craft</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1522071820081-9d91a554f212.jpg",   alt: "Winsil team collaborating", label: "Our Team" },
              { src: "/images/photo-1573164713988-8665fc963095.jpg",   alt: "Agile sprint planning",     label: "Agile Culture" },
              { src: "/images/photo-1531403009284-440d08085b32.jpg",   alt: "System architecture design", label: "System Design" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1461749280684-dab374f5cde3.jpg", alt: "Code architecture",        label: "Engineering" },
              { src: "/images/photo-1516321318423-f06f85e504b3.jpg", alt: "Client project delivery",  label: "Client Delivery" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s) => (
            <div key={s.label}>
              {s.icon}
              <p className="text-4xl sm:text-5xl font-black">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mission & Vision ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 text-center mb-2">Purpose</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">Mission & Vision</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="group bg-gray-950 rounded-2xl p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-orange-400" />
              </div>
              <h3 className="text-2xl font-extrabold text-white mb-4">Our Mission</h3>
              <p className="text-gray-400 text-base leading-relaxed">
                To empower businesses through robust, scalable, and innovative software and branding solutions that drive measurable growth — delivered with transparency and engineering integrity.
              </p>
            </div>
            <div className="group bg-gray-950 rounded-2xl p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-orange-400" />
              </div>
              <h3 className="text-2xl font-extrabold text-white mb-4">Our Vision</h3>
              <p className="text-gray-400 text-base leading-relaxed">
                To be a globally recognised partner in software engineering and digital branding — known for technical excellence, creative depth, and the measurable impact we deliver for every client.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Core Values ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 text-center mb-2">What Drives Us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">Our Core Values</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center mb-5">
                  {v.icon}
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Winsil ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Why Winsil</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                Built for Teams That{" "}
                <span className="text-orange-500">Demand More</span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We're not a vendor — we're an extension of your team. From discovery to deployment, we bring senior-level thinking, honest communication, and the discipline to ship on time.
              </p>
              <ul className="space-y-3 mb-10">
                {whyPoints.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/25"
              >
                Work With Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/photo-1542744094-24638eff58bb.jpg"
                alt="Winsil engineers at work"
                className="w-full h-[440px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/70 via-transparent to-orange-500/10" />
              <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black text-orange-600">100+</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Projects Delivered</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Team Pillars ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-400 text-center mb-2">Our Team</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-14">What Makes Our Team Different</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamPillars.map((p, i) => (
              <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-8 hover:border-orange-500/40 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-orange-500/10 border border-orange-500/20 rounded-xl flex items-center justify-center mb-5">
                  {p.icon}
                </div>
                <h3 className="text-lg font-extrabold text-white mb-3">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Second image row ── */}
      <div className="py-14 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { src: "/images/photo-1605810230434-7631ac76ec81.jpg", alt: "DevOps pipeline",        label: "DevOps & CI/CD" },
            { src: "/images/photo-1517180102446-e50d6c1e370b.jpg", alt: "Cloud infrastructure",  label: "Cloud Infrastructure" },
            { src: "/images/photo-1558494949-ef010cbdcc31.jpg",    alt: "Databases and systems", label: "Data & Systems" },
          ].map((img, i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gray-950 py-16 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-orange-400" />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-orange-400 mb-3">Let's Build Together</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Ready to Start Your Project?</h2>
          <p className="text-white/50 mb-8">Tell us what you're building and we'll come back with a plan in 48 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/30"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/software-development")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full border border-white/20 text-white hover:border-orange-400 hover:text-orange-400 transition-all duration-300"
            >
              View Our Work
              <Star className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default CompanyOverview;
